import {
    useCallback,
    useEffect,
    useState,
    useRef
} from "react";
import {
    ActivityIndicator,
    TouchableOpacity,
    View
} from "react-native";
import type {
    OldPresidentPayloadType,
    OldPresidentsViewProps
} from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitSnackBar,
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    TextInput,
    Dialog,
    Button,
    Text
} from "ncore-ui-kit";
import {
    CameraIcon,
    PencilIcon,
    Trash2Icon,
    PlusIcon,
    UserIcon
} from "lucide-react-native";
import RESTServiceContext from "../../../../services/restAPI/context";
import SmartCoverImage from "../../../../components/smartCoverImage";

const OldPresidentsView = ({
    refreshKey
}: OldPresidentsViewProps) => {
    const {
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const photoInputRef = useRef<HTMLInputElement>(null);

    const [
        oldPresidents,
        setOldPresidents
    ] = useState<OldPresidentPayloadType[]>([]);

    const [
        isLoading,
        setIsLoading
    ] = useState<boolean>(true);

    const [
        isSubmitting,
        setIsSubmitting
    ] = useState<boolean>(false);

    const [
        isFormDialogVisible,
        setIsFormDialogVisible
    ] = useState<boolean>(false);

    const [
        isDeleteDialogVisible,
        setIsDeleteDialogVisible
    ] = useState<boolean>(false);

    const [
        photoFile,
        setPhotoFile
    ] = useState<File | null>(null);

    const [
        photoPreview,
        setPhotoPreview
    ] = useState<string>("");

    const [
        selectedPresident,
        setSelectedPresident
    ] = useState<OldPresidentPayloadType | null>(null);

    const [
        fullName,
        setFullName
    ] = useState<string>("");

    const [
        term,
        setTerm
    ] = useState<string>("");

    const [
        order,
        setOrder
    ] = useState<string>("0");

    const [
        profilePhotoPath,
        setProfilePhotoPath
    ] = useState<string>("");

    const getImageUrl = (path?: string) => {
        if(!path) {
            return "";
        }
        if(path.startsWith("http://") || path.startsWith("https://") || path.startsWith("blob:")) {
            return path;
        }
        return `${process.env.STORAGE_URL}/uploads${path.startsWith("/") ? "" : "/"}${path}`;
    };

    const uploadPhoto = async (file: File): Promise<string> => {
        const tokenResult = await RESTServiceContext.action("getUploadTokenForContentPhoto", {
            fileType: file.type || "image/jpeg",
            fileName: file.name,
            fileSize: file.size
        });
        const uploadToken = tokenResult.payload?.token;
        if(!uploadToken) {
            throw new Error("No upload token received");
        }
        const formData = new FormData();
        formData.append("file", file);
        const uploadResponse = await fetch(`${process.env.STORAGE_URL}/upload`, {
            headers: {
                Authorization: uploadToken
            },
            body: formData,
            method: "POST"
        });
        if(!uploadResponse.ok) {
            throw new Error("Storage upload failed");
        }
        const uploadJson = await uploadResponse.json();
        return uploadJson?.payload?.key || "";
    };

    const fetchOldPresidents = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await RESTServiceContext.action("getAllOldPresidents", null);
            if(Array.isArray(response?.payload)) {
                const sorted = [...response.payload].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                setOldPresidents(sorted);
            }
        } catch {
            NCoreUIKitSnackBar.open({
                title: localize("old-presidents-fetch-error")
            });
        } finally {
            setIsLoading(false);
        }
    }, [
        localize
    ]);

    useEffect(() => {
        fetchOldPresidents();
    }, [
        fetchOldPresidents,
        refreshKey
    ]);

    const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if(file) {
            const maxFileSize = 5 * 1024 * 1024;
            if(file.size > maxFileSize) {
                NCoreUIKitSnackBar.open({
                    title: localize("members-invalid-file-size") || "Dosya boyutu 5 MB'dan küçük olmalıdır."
                });
                return;
            }
            setPhotoFile(file);
            setPhotoPreview(URL.createObjectURL(file));
        }
    };

    const handleRemovePhoto = () => {
        setProfilePhotoPath("");
        setPhotoPreview("");
        setPhotoFile(null);
        if(photoInputRef.current) {
            photoInputRef.current.value = "";
        }
    };

    const handleOpenCreate = () => {
        setSelectedPresident(null);
        setFullName("");
        setTerm("");
        setOrder(String(oldPresidents.length + 1));
        setProfilePhotoPath("");
        setPhotoFile(null);
        setPhotoPreview("");
        if(photoInputRef.current) {
            photoInputRef.current.value = "";
        }
        setIsFormDialogVisible(true);
    };

    const handleOpenEdit = (pres: OldPresidentPayloadType) => {
        setSelectedPresident(pres);
        setFullName(pres.fullName || "");
        setTerm(pres.term || "");
        setOrder(String(pres.order ?? 0));
        setProfilePhotoPath(pres.profilePhotoPath || "");
        setPhotoFile(null);
        setPhotoPreview("");
        if(photoInputRef.current) {
            photoInputRef.current.value = "";
        }
        setIsFormDialogVisible(true);
    };

    const handleSave = async () => {
        if(!fullName.trim()) {
            NCoreUIKitSnackBar.open({
                title: localize("old-president-name-required")
            });
            return;
        }

        if(!term.trim()) {
            NCoreUIKitSnackBar.open({
                title: localize("old-president-term-required")
            });
            return;
        }

        setIsSubmitting(true);
        try {
            let finalPhotoPath = profilePhotoPath;
            if(photoFile) {
                const uploadedKey = await uploadPhoto(photoFile);
                if(uploadedKey) {
                    finalPhotoPath = uploadedKey;
                }
            }

            const parsedOrder = Number(order) || 0;
            if(selectedPresident) {
                const response = await RESTServiceContext.action("updateOldPresident", {
                    profilePhotoPath: finalPhotoPath || undefined,
                    id: selectedPresident._id,
                    order: parsedOrder,
                    fullName,
                    term
                });

                if(response?.isError) {
                    NCoreUIKitSnackBar.open({
                        title: response?.message || localize("old-president-save-error")
                    });
                    return;
                }

                NCoreUIKitSnackBar.open({
                    title: localize("old-president-update-success")
                });
            } else {
                const response = await RESTServiceContext.action("createOldPresident", {
                    profilePhotoPath: finalPhotoPath || undefined,
                    order: parsedOrder,
                    fullName,
                    term
                });

                if(response?.isError) {
                    NCoreUIKitSnackBar.open({
                        title: response?.message || localize("old-president-save-error")
                    });
                    return;
                }

                NCoreUIKitSnackBar.open({
                    title: localize("old-president-create-success")
                });
            }

            setPhotoFile(null);
            setPhotoPreview("");
            setIsFormDialogVisible(false);
            fetchOldPresidents();
        } catch {
            NCoreUIKitSnackBar.open({
                title: localize("old-president-save-error")
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async () => {
        if(!selectedPresident) {
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await RESTServiceContext.action("deleteOldPresident", {
                id: selectedPresident._id
            });

            if(response?.isError) {
                NCoreUIKitSnackBar.open({
                    title: response?.message || localize("old-president-delete-error")
                });
                return;
            }

            NCoreUIKitSnackBar.open({
                title: localize("old-president-delete-success")
            });
            setIsDeleteDialogVisible(false);
            fetchOldPresidents();
        } catch {
            NCoreUIKitSnackBar.open({
                title: localize("old-president-delete-error")
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderHeader = () => {
        return <View
            style={[
                stylesheet.headerContainer,
                {
                    marginBottom: spaces.spacingMd
                }
            ]}
        >
            <View>
                <Text
                    variant="titleMediumSize"
                    color="high"
                >
                    {localize("old-presidents-title")}
                </Text>
                <Text
                    variant="bodySmallSize"
                    color="disabled"
                >
                    {`${oldPresidents.length} ${localize("old-presidents-count")}`}
                </Text>
            </View>
            <Button
                customTextColor={colors.project.brandWhite}
                title={localize("old-presidents-add-new")}
                icon={({
                    size
                }) => <PlusIcon
                    color={colors.project.brandWhite}
                    size={size}
                />}
                onPress={handleOpenCreate}
                spreadBehaviour="free"
                variant="filled"
                type="primary"
            />
        </View>;
    };

    const renderEmpty = () => {
        return <View
            style={[
                stylesheet.emptyContainer,
                {
                    backgroundColor: colors.project.brandWhite,
                    borderColor: colors.project.brandBorder,
                    borderWidth: borders.seperator,
                    borderRadius: radiuses.md,
                    padding: spaces.spacingXl
                }
            ]}
        >
            <Text
                variant="bodyMediumSize"
                color="disabled"
            >
                {localize("old-presidents-empty")}
            </Text>
        </View>;
    };

    const renderList = () => {
        if(oldPresidents.length === 0) {
            return renderEmpty();
        }

        return <View
            style={[
                stylesheet.listContainer,
                {
                    gap: spaces.spacingSm
                }
            ]}
        >
            {oldPresidents.map((pres) => {
                const photoUrl = getImageUrl(pres.profilePhotoPath);

                return <View
                    style={[
                        stylesheet.presidentCard,
                        {
                            backgroundColor: colors.project.brandWhite,
                            borderColor: colors.project.brandBorder,
                            borderWidth: borders.seperator,
                            borderRadius: radiuses.md,
                            padding: spaces.spacingMd
                        }
                    ]}
                    key={pres._id}
                >
                    <View
                        style={[
                            stylesheet.presidentInfo,
                            {
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        <View
                            style={[
                                stylesheet.presidentPhotoBox,
                                {
                                    backgroundColor: colors.content.container.subtle,
                                    borderColor: colors.content.border.subtle,
                                    borderRadius: radiuses.sm
                                }
                            ]}
                        >
                            {
                                photoUrl ?
                                    <SmartCoverImage
                                        containerStyle={stylesheet.presidentPhoto}
                                        imageStyle={stylesheet.presidentPhoto}
                                        url={photoUrl}
                                    />
                                :
                                    <UserIcon
                                        color={colors.content.text.disabled}
                                        size={24}
                                    />
                            }
                        </View>
                        <View
                            style={[
                                stylesheet.presidentDetails,
                                {
                                    gap: spaces.spacingXs
                                }
                            ]}
                        >
                            <Text
                                variant="titleSmallSize"
                                color="high"
                            >
                                {pres.fullName}
                            </Text>
                            <Text
                                variant="bodySmallSize"
                                color="disabled"
                            >
                                {pres.term}
                            </Text>
                        </View>
                    </View>
                    <View
                        style={[
                            stylesheet.actionsContainer,
                            {
                                gap: spaces.spacingXs
                            }
                        ]}
                    >
                        <View
                            style={[
                                stylesheet.orderBadge,
                                {
                                    backgroundColor: colors.content.container.subtle,
                                    paddingHorizontal: spaces.spacingSm,
                                    borderRadius: radiuses.full
                                }
                            ]}
                        >
                            <Text
                                variant="bodySmallSize"
                                color="disabled"
                            >
                                #{pres.order ?? 0}
                            </Text>
                        </View>
                        <Button
                            icon={({
                                color,
                                size
                            }) => <PencilIcon
                                color={colors.content.icon[color]}
                                size={size}
                            />}
                            onPress={() => handleOpenEdit(pres)}
                            spreadBehaviour="free"
                            variant="ghost"
                        />
                        <Button
                            icon={({
                                color,
                                size
                            }) => <Trash2Icon
                                color={colors.content.icon[color]}
                                size={size}
                            />}
                            onPress={() => {
                                setSelectedPresident(pres);
                                setIsDeleteDialogVisible(true);
                            }}
                            spreadBehaviour="free"
                            variant="ghost"
                            type="danger"
                        />
                    </View>
                </View>;
            })}
        </View>;
    };

    const renderFormDialog = () => {
        const displayPhotoUrl = photoPreview || getImageUrl(profilePhotoPath);

        return <Dialog
            title={selectedPresident ? localize("old-president-edit-title") : localize("old-president-create-title")}
            primaryButtonProps={{
                title: selectedPresident ? localize("save") || "Kaydet" : localize("create") || "Oluştur",
                isLoading: isSubmitting,
                onPress: handleSave
            }}
            contentContainerStyle={stylesheet.dialogContentContainer}
            secondaryButtonProps={{
                onPress: () => setIsFormDialogVisible(false),
                customTextColor: colors.project.brandWhite,
                customBorderColor: "secondaryButton",
                title: localize("cancel") || "İptal",
                customColor: "secondaryButton",
                variant: "filled"
            }}
            onClosed={() => setIsFormDialogVisible(false)}
            isVisible={isFormDialogVisible}
            variant="yes-no"
        >
            <View
                style={[
                    stylesheet.dialogForm,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <input
                    style={stylesheet.photoInput}
                    onChange={handlePhotoSelect}
                    ref={photoInputRef}
                    accept="image/*"
                    type="file"
                />
                <TouchableOpacity
                    style={[
                        stylesheet.dialogPhotoBox,
                        {
                            backgroundColor: colors.content.container.subtle,
                            borderColor: colors.content.border.subtle,
                            borderRadius: radiuses.md
                        }
                    ]}
                    onPress={() => photoInputRef.current?.click()}
                >
                    {
                        displayPhotoUrl ?
                            <SmartCoverImage
                                containerStyle={stylesheet.presidentPhoto}
                                imageStyle={stylesheet.presidentPhoto}
                                url={displayPhotoUrl}
                            />
                        :
                            <View
                                style={[
                                    stylesheet.photoPlaceholderContent,
                                    {
                                        gap: spaces.spacingXs
                                    }
                                ]}
                            >
                                <CameraIcon
                                    color={colors.content.text.disabled}
                                    size={28}
                                />
                                <Text
                                    variant="bodySmallSize"
                                    color="disabled"
                                >
                                    {localize("content-select-photo")}
                                </Text>
                            </View>
                    }
                </TouchableOpacity>
                {Boolean(displayPhotoUrl) && <Button
                    title={localize("content-remove-photo")}
                    icon={({
                        color,
                        size
                    }) => <Trash2Icon
                        color={colors.content.icon[color]}
                        size={size}
                    />}
                    onPress={handleRemovePhoto}
                    spreadBehaviour="free"
                    variant="filled"
                    type="danger"
                />}
                <TextInput
                    placeholder={localize("old-president-fullname-placeholder")}
                    title={localize("old-president-fullname-label")}
                    onChangeText={setFullName}
                    spreadBehaviour="stretch"
                    value={fullName}
                />
                <TextInput
                    placeholder={localize("old-president-term-placeholder")}
                    title={localize("old-president-term-label")}
                    spreadBehaviour="stretch"
                    onChangeText={setTerm}
                    value={term}
                />
                <TextInput
                    placeholder={localize("old-president-order-placeholder")}
                    title={localize("old-president-order-label")}
                    spreadBehaviour="stretch"
                    onChangeText={setOrder}
                    keyboardType="numeric"
                    value={order}
                />
            </View>
        </Dialog>;
    };

    const renderDeleteDialog = () => {
        return <Dialog
            contentContainerStyle={stylesheet.dialogContentContainer}
            secondaryButtonProps={{
                onPress: () => setIsDeleteDialogVisible(false),
                customTextColor: colors.project.brandWhite,
                customBorderColor: "secondaryButton",
                title: localize("cancel") || "İptal",
                customColor: "secondaryButton",
                variant: "filled"
            }}
            onClosed={() => setIsDeleteDialogVisible(false)}
            title={localize("old-president-delete-title")}
            primaryButtonProps={{
                title: localize("delete") || "Sil",
                isLoading: isSubmitting,
                onPress: handleDelete,
                type: "danger"
            }}
            isVisible={isDeleteDialogVisible}
            variant="yes-no"
        >
            <Text
                variant="bodyMediumSize"
                color="high"
            >
                {`${selectedPresident?.fullName || ""} ${localize("old-president-delete-confirm")}`}
            </Text>
        </Dialog>;
    };

    if(isLoading) {
        return <View
            style={stylesheet.loadingContainer}
        >
            <ActivityIndicator
                color={colors.project.gold}
                size="large"
            />
        </View>;
    }

    return <View
        style={[
            stylesheet.container,
            {
                gap: spaces.spacingLg
            }
        ]}
    >
        {renderHeader()}
        {renderList()}
        {renderFormDialog()}
        {renderDeleteDialog()}
    </View>;
};
export default OldPresidentsView;
